import { App as AntdApp, Button, Flex, Tag, Typography } from 'antd'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { WorkspaceAgent } from 'src/api/facade/types'
import type { SquadAgentMcpLink } from 'src/services/mcpServers'
import { useShowApiErrors } from 'src/utils'

import {
  parseAllowedScopes,
  serializeAllowedScopes,
  type ScopeRow
} from './scopeModel'
import { ScopePicker } from './ScopePicker'
import { useSetAgentMcpScopes } from './useAgentConnections'

const { Text } = Typography

type Props = {
  workspaceId: number
  agent: WorkspaceAgent
  /** The per-(agent, server) link whose `allowed_scopes` this edits. */
  link: SquadAgentMcpLink
  /** Scope-catalog rows for THIS connection's app (`core`/`crm`/`hrm`). */
  appCatalog: ScopeRow[]
  readOnly: boolean
  /** Expand on mount (used to nudge setup right after connecting). */
  defaultOpen?: boolean
}

/**
 * Inline per-connection permission editor, rendered under an MCP server row.
 * Scopes are per connection (per app instance): the picker is filtered to that
 * server's app namespace and saves to the link, so connecting a tool and
 * granting its permissions happen in one place.
 */
export const ConnectionScopeEditor = ({
  workspaceId,
  agent,
  link,
  appCatalog,
  readOnly,
  defaultOpen = false
}: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const showApiErrors = useShowApiErrors()
  const mutation = useSetAgentMcpScopes(workspaceId, agent.id)

  const [open, setOpen] = useState(defaultOpen)

  const savedRaw = link.allowed_scopes ?? ''
  // Draft re-synced when the link's saved scopes change (e.g. after a save) —
  // React's "reset derived state when a prop changes" pattern.
  const [state, setState] = useState<{ raw: string; draft: Set<string> }>(
    () => ({ raw: savedRaw, draft: parseAllowedScopes(savedRaw) })
  )
  if (state.raw !== savedRaw) {
    setState({ raw: savedRaw, draft: parseAllowedScopes(savedRaw) })
  }
  const { draft } = state
  const setDraft = (next: Set<string>) =>
    setState((s) => ({ ...s, draft: next }))

  const saved = parseAllowedScopes(savedRaw)
  const dirty =
    draft.size !== saved.size ||
    Array.from(draft).some((code) => !saved.has(code))

  const total = appCatalog.length
  const granted = appCatalog.filter((r) => draft.has(r.code)).length

  const save = () =>
    mutation.mutate(
      { linkId: link.id, scopes: serializeAllowedScopes(draft) },
      {
        onSuccess: () =>
          message.success(t('workspace.agents.messages.scopesUpdated')),
        onError: (e) => showApiErrors(e)
      }
    )

  return (
    <div
      style={{
        padding: '4px 12px 8px 48px',
        borderBottom: '1px solid var(--ant-color-fill-tertiary)'
      }}
    >
      <Flex align="center" justify="space-between" gap={8}>
        <Button
          type="text"
          size="small"
          style={{ padding: 0, height: 'auto' }}
          onClick={() => setOpen((v) => !v)}
        >
          <Flex align="center" gap={6}>
            {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <Text style={{ fontSize: 12 }}>
              {t('workspace.agents.mcp.permissions')}
            </Text>
            <Tag
              style={{
                marginInlineEnd: 0,
                fontSize: 10,
                fontVariantNumeric: 'tabular-nums'
              }}
            >
              {granted} / {total}
            </Tag>
          </Flex>
        </Button>
        {open && dirty ? (
          <Button
            size="small"
            type="primary"
            disabled={readOnly}
            loading={mutation.isPending}
            onClick={save}
          >
            {t('workspace.agents.access.save')}
          </Button>
        ) : null}
      </Flex>

      {open ? (
        <div style={{ marginTop: 8 }}>
          {total === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.agents.access.noScopesForApp')}
            </Text>
          ) : (
            <ScopePicker
              catalog={appCatalog}
              value={draft}
              onChange={setDraft}
              disabled={readOnly}
            />
          )}
        </div>
      ) : null}
    </div>
  )
}
